import { z } from 'zod';
import { Category, DraftKind, MaterialKind, OfferResponse, OrchestraHistory, PrincipalPreference, ProfessionalOrAmateur, Scoring, } from './domain.js';
import { UploadProblem } from './ranking.js';
/** The auditions API. Designed here; the contract specifies only drafts. */
export const AuditionScope = z.enum([
    'auditions:apply', 'auditions:adjudicate', 'auditions:review', 'auditions:manage',
]);
/* ── Drafts: the one debounced autosave endpoint ─────────────────────────── */
export const DraftPutRequest = z.object({
    contents: z.record(z.string(), z.unknown()),
    /** The version the client loaded. A mismatch is a 409, so the page can warn. */
    version: z.number().int().nonnegative(),
});
export const DraftResponse = z.object({
    kind: DraftKind,
    recordId: z.string(),
    contents: z.record(z.string(), z.unknown()),
    version: z.number().int(),
    updatedAt: z.string().datetime(),
});
/* ── Video links ─────────────────────────────────────────────────────────── */
export const VideoCheckRequest = z.object({
    url: z.string().url(),
    kind: MaterialKind,
});
export const VideoCheckResponse = z.object({
    provider: z.enum(['youtube', 'vimeo']),
    videoId: z.string(),
    videoHash: z.string().nullable(),
    /** Built from parsed IDs only, never from the submitted URL. */
    embedUrl: z.string().url(),
    linkStatus: z.enum(['ok', 'private', 'deleted', 'no_embed', 'unknown']),
    title: z.string().nullable(),
});
/* ── Application ─────────────────────────────────────────────────────────── */
/**
 * One of the four videos.
 *
 * The two contrasting pieces are the applicant's own choice, so the title and
 * composer are asked of them and required: an adjudicator opening a card
 * otherwise sees "Piece 1" and a link, and has to play it to find out what it
 * is, while the person who sent it knew the answer in a second. The prepared
 * excerpt is the same piece for everybody and the fourth video is somebody
 * talking, so neither has a title to give.
 */
const MaterialInput = z.object({
    kind: MaterialKind,
    title: z.string().max(200).optional(),
    composer: z.string().max(200).optional(),
    url: z.string().url(),
}).superRefine((material, ctx) => {
    if (material.kind !== 'piece_1' && material.kind !== 'piece_2')
        return;
    for (const field of ['title', 'composer']) {
        if (material[field]?.trim())
            continue;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: `Please give the ${field} of this piece.`,
        });
    }
});
/**
 * Every field in the applicant card table. Email is absent on purpose: it comes
 * from the verified Logto account, and ground rule 4 forbids a user table.
 */
export const SubmitApplicationRequest = z.object({
    auditionId: z.string().min(1),
    sectionId: z.string().min(1),
    /* ── personal details ─────────────────────────────────────────────────── */
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    pronouns: z.string().min(1).max(60),
    /** Asked, and read by the team rather than by an adjudicator. */
    accessNeeds: z.string().max(2000).optional(),
    /** The same: for looking after somebody on the day, not for judging them. */
    dietaryNeeds: z.string().max(2000).optional(),
    /** "City, State" on last year's form, which is what the label asks for. */
    location: z.string().min(1).max(200),
    country: z.string().min(1).max(80),
    /** A mobile number is optional; the SMS opt-in below is what needs one. */
    phone: z.string().max(40).optional(),
    mailingListOptIn: z.boolean(),
    smsOptIn: z.boolean(),
    /* ── musical information ──────────────────────────────────────────────── */
    orchestraHistory: OrchestraHistory,
    /*
     * Asked only of somebody who has played in an orchestra, which is why it is
     * optional here and required by the branch rather than by the type. The old
     * `currentOrchestraInvolvement` and `previousParticipation` are no longer
     * asked; their columns keep what earlier applications said.
     */
    orchestralExperience: z.string().max(5000).optional(),
    professionalOrAmateur: ProfessionalOrAmateur,
    /*
     * The answer to the section's doubling question, and what they said about
     * it. Optional here because most sections do not ask: the API requires it
     * of the ones that do, which is where the question lives.
     */
    doubles: z.boolean().optional(),
    doublingDetail: z.string().max(500).optional(),
    currentOrchestraInvolvement: z.string().max(2000).optional(),
    previousParticipation: z.boolean().optional(),
    principalPreference: PrincipalPreference,
    leadershipExperience: z.string().max(5000).optional(),
    whyJoin: z.string().max(5000).optional(),
    /* ── videos, availability, terms ──────────────────────────────────────── */
    availabilityConfirmed: z.literal(true),
    reserveListWilling: z.boolean(),
    termsAccepted: z.literal(true),
    privacyAccepted: z.literal(true),
    /*
     * Four: the prepared excerpt, two contrasting pieces, and a short one saying
     * why they would like to be part of the orchestra. Last year that fourth
     * video is what replaced a written answer.
     *
     * None, where the applicant has sent them by file request instead — see
     * below. Anything they did manage to link is still kept and still checked.
     */
    materials: z.array(MaterialInput).max(4),
    /**
     * They are sending the videos by file request rather than by link.
     *
     * Set by pressing the button on the video page, which records this before
     * handing them to Dropbox — so a tab that never comes back has still said
     * where its videos are. The adjudicator's card says so, with a link to the
     * folder, because a card with four empty video slots and no explanation
     * reads as an application nobody finished.
     */
    videosViaDropbox: z.boolean().optional(),
}).superRefine((application, ctx) => {
    /*
     * Four links, or a file request. Not three links and a shrug: the whole
     * point of the flag is that somebody said which of the two they are doing.
     */
    if (application.videosViaDropbox)
        return;
    if (application.materials.length === 4)
        return;
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['materials'],
        message: 'Please give a link to each of the four videos, or send them by file request.',
    });
});
/**
 * The order somebody would rather play their instruments in, when they have
 * applied for more than one.
 *
 * The whole order, every time, rather than one application's position: a rank
 * only means anything against the others, and sending them together is what
 * makes it impossible to end up with two first choices or a gap at number two.
 */
export const InstrumentPreferenceRequest = z.object({
    auditionId: z.string().min(1),
    /** Their applications to that audition, first choice first. */
    order: z.array(z.string().min(1)).min(2).max(20),
});
/**
 * Settings that belong to the deployment rather than to an audition: the door
 * on the public site, and where videos go when links will not do.
 *
 * The gate's password is not here and never will be. It is read by the web
 * service from its own environment, so that turning the door on and off is a
 * decision the team can make in the app, while the key itself is never in a
 * database, a backup or an API response.
 */
export const SiteSettingsUpdate = z.object({
    siteGateEnabled: z.boolean().optional(),
    dropboxFileRequestUrl: z.string().max(500).optional(),
    dropboxFolderUrl: z.string().max(500).optional(),
});
/* ── Adjudication ────────────────────────────────────────────────────────── */
export const AdjudicationRow = z.object({
    applicationId: z.string(),
    notes: z.string().max(10000).nullable(),
    part: z.string().nullable(),
    /** Written only when the section's scoring is part_and_score. */
    score: z.number().nullable(),
    category: Category.nullable(),
    /** For the violin panel this is the section Suzie assigned, not applied to. */
    sectionId: z.string().nullable(),
    rank: z.number().int().positive().nullable(),
    leadershipPotential: z.boolean(),
});
/**
 * Every unmet precondition is returned at once, not the first one. The problem
 * list comes from `ranking.ts`, which is what actually derives them — two
 * copies of that enum would eventually disagree.
 */
export const UploadBlocker = z.object({
    sectionId: z.string(),
    problem: UploadProblem,
    applicationIds: z.array(z.string()).optional(),
});
/* ── Conductor review ────────────────────────────────────────────────────── */
/** `note` is required on every conductor edit. */
export const SelectionEditRequest = z.object({
    changes: z
        .array(z.object({
        applicationId: z.string(),
        rank: z.number().int().positive().optional(),
        category: Category.optional(),
        sectionId: z.string().optional(),
        principalPriority: z.boolean().optional(),
    }))
        .min(1),
    note: z.string().min(1).max(2000),
});
export const RequestChangesRequest = z.object({ comment: z.string().min(1).max(5000) });
/* ── Offers ──────────────────────────────────────────────────────────────── */
export const OfferRespondRequest = z.object({
    response: OfferResponse.exclude(['no_response']),
});
/* ── Release ─────────────────────────────────────────────────────────────── */
export const ReleasePreviewResponse = z.object({
    counts: z.object({
        selected: z.number().int(),
        principal: z.number().int(),
        reserve: z.number().int(),
        unsuccessful: z.number().int(),
        total: z.number().int(),
    }),
    blockers: z.array(z.object({
        kind: z.enum([
            'not_signed_off', 'principals_not_chosen',
            'unresolved_multi_seat', 'template_unapproved',
        ]),
        detail: z.string(),
    })),
});
/* ── Setup ───────────────────────────────────────────────────────────────── */
/**
 * Where a section's prepared excerpt lives.
 *
 * Either a path on this site -- the excerpts ship with the app, so
 * `/excerpts/viola.png` -- or an address somewhere else, for a team that keeps
 * them in their own storage.
 *
 * A path rather than a full URL for the ones we serve, because the data should
 * not carry the hostname: the same database restored behind a different domain
 * would otherwise hand applicants links to the old one. `z.string().url()`
 * would have forced the hostname in.
 *
 * `//host/path` is rejected. It looks like a path and is a URL to somewhere
 * else entirely, which is exactly the shape somebody would use to point an
 * applicant off-site while appearing not to.
 */
export const ExcerptLocation = z.string().min(1).max(2000).refine((value) => {
    if (value.startsWith('//'))
        return false;
    if (value.startsWith('/'))
        return true;
    try {
        return ['http:', 'https:'].includes(new URL(value).protocol);
    }
    catch {
        return false;
    }
}, { message: 'Give a web address, or a path on this site beginning with /' });
export const CreateSectionRequest = z.object({
    name: z.string().min(1).max(100),
    instrument: z.string().min(1).max(100),
    placesRequired: z.number().int().nonnegative(),
    parts: z.array(z.string().min(1)).default([]),
    preparedExcerpt: z.string().max(2000).optional(),
    excerptUrl: ExcerptLocation.optional(),
    instrumentNotes: z.string().max(2000).optional(),
    /** Asked of everybody applying to this section. Empty asks nothing. */
    doublingQuestion: z.string().max(300).optional(),
    /** What a part in this section may come with: `piccolo`, `bass trombone`. */
    doublingInstrument: z.string().max(100).optional(),
    scoring: Scoring.default('part'),
    isEntrySection: z.boolean().default(true),
    assignsSubsection: z.boolean().default(false),
});
//# sourceMappingURL=auditions.js.map